export class GDSNavigationEventHandler {
    constructor(builder) {
        this.builder = builder;
        this.state = builder.stateManager;
        this.api = builder.apiService;
        this.orchestrator = builder.stepOrchestrator;

        // Flag to prevent duplicate processing
        this.isProcessingTransition = false;

        this.boundHandlers = {
            handleBackClick: this.handleBackClick.bind(this),
            handleNextClick: this.handleNextClick.bind(this),
            handleRefreshClick: this.handleRefreshClick.bind(this),
            handlePopState: this.handlePopState.bind(this),
            handleStepClick: this.handleStepClick.bind(this),
            handleEnvironmentCardClick: this.handleEnvironmentCardClick.bind(this)
        };
    }

    setupListeners() {
        const { contentContainer, backButton, progressContainer } = this.builder;

        // Listen for next clicks via delegation
        contentContainer?.addEventListener('click', e => {
            if (e.target.closest('#inline-next-btn')) {
                this.boundHandlers.handleNextClick(e);
            }
        });

        // Listen for environment card clicks
        contentContainer?.addEventListener('click', e => {
            const envCard = e.target.closest('.environment-card');
            if (envCard && !e.target.closest('.info-icon-container')) {
                this.boundHandlers.handleEnvironmentCardClick(e, envCard);
            }
        });

        // Listen for back clicks
        backButton?.addEventListener('click', this.boundHandlers.handleBackClick);
        progressContainer?.querySelector('#mobile-back-btn')?.addEventListener('click', this.boundHandlers.handleBackClick);

        // Other top-level navigation
        document.getElementById('refresh-icon')?.addEventListener('click', this.boundHandlers.handleRefreshClick);
        window.addEventListener('popstate', this.boundHandlers.handlePopState);
        this.setupProgressStepListeners();
    }

    removeListeners() {
        const { backButton, progressContainer } = this.builder;
        backButton?.removeEventListener('click', this.boundHandlers.handleBackClick);
        progressContainer?.querySelector('#mobile-back-btn')?.removeEventListener('click', this.boundHandlers.handleBackClick);
        document.getElementById('refresh-icon')?.removeEventListener('click', this.boundHandlers.handleRefreshClick);
        window.removeEventListener('popstate', this.boundHandlers.handlePopState);
    }

    setupProgressStepListeners() {
        this.builder.progressContainer?.querySelectorAll('[data-step]').forEach(stepElement => {
            const circle = stepElement.querySelector('.step-circle');
            const stepNumber = parseInt(stepElement.getAttribute('data-step'), 10);
            circle?.addEventListener('click', () => this.boundHandlers.handleStepClick(stepNumber));
        });
    }

    async processStepTransition(getContextFn) {
        // Prevent duplicate processing
        if (this.isProcessingTransition) {
            console.warn('[GDSNavigationEventHandler] Transition already in progress, skipping...');
            return;
        }

        const updatedContext = getContextFn();
        if (!updatedContext) return;

        this.isProcessingTransition = true;
        this.builder.uiFeedback.showLoading();
        const oldStep = this.state.currentStep;

        try {
            // Set flag to indicate we're in a step transition
            this.state._isStepTransition = true;

            const responseData = await this.api.fetchData(updatedContext);
            this.state.updateStateFromApiResponse(responseData);
            const newStep = this.state.currentStep;

            // Update skip flag
            if (oldStep === 1 && newStep === 3) {
                this.state.wasStep2Skipped = true;
            } else if (newStep <= 2) {
                this.state.wasStep2Skipped = false;
            }

            // Single render call
            await this.orchestrator.renderCurrentStep();

            // Handle snapshot creation only if step actually changed
            if (newStep !== oldStep) {
                await this.handleSnapshotCreation(oldStep, newStep);
            }

        } catch (error) {
            console.error("Error during step transition:", error);
            this.builder.uiFeedback.showError('There was a problem moving to the next step.');
        } finally {
            this.state._isStepTransition = false;
            this.isProcessingTransition = false;
            this.builder.uiFeedback.hideLoading();
        }
    }

    async handleNextClick(e) {
        e?.preventDefault();
        e?.stopPropagation(); // Prevent event bubbling
        await this.processStepTransition(() => this.orchestrator.collectFormData());
    }

    async handleEnvironmentCardClick(e, envCard) {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling

        const apiValue = envCard.getAttribute('data-environment-api');
        if (!apiValue) return;

        const envInput = this.builder.contentContainer.querySelector('input[data-field="environment"]');
        if (envInput) {
            envInput.value = apiValue;
        } else {
            console.error('[GDSNavigationEventHandler] Environment input field not found!');
            return;
        }

        await this.processStepTransition(() => this.orchestrator.collectFormData());
    }

    async handleBackClick() {
        if (this.state.isLoading || this.isProcessingTransition) return;

        if (this.state.currentStep === 3 && this.state.wasStep2Skipped) {
            await this.handleStepClick(1);
        } else {
            window.history.back();
        }
    }

    async handleRefreshClick() {
        if (this.isProcessingTransition) return;
        await this.handleStepClick(1);
    }

    async handleStepClick(stepNumber) {
        if (this.state.isLoading || this.isProcessingTransition || stepNumber === this.state.currentStep) return;
        if (stepNumber === 2 && this.state.wasStep2Skipped) return;

        if (stepNumber < this.state.currentStep && this.state.completedSteps.includes(stepNumber)) {
            this.isProcessingTransition = true;
            this.builder.uiFeedback.showLoading();

            try {
                const contextForTargetStep = this.getContextForStep(stepNumber);
                const responseData = await this.api.fetchData(contextForTargetStep);

                this.state.updateStateFromApiResponse(responseData);
                this.state.currentStep = stepNumber;

                // Update completedSteps
                this.state.completedSteps = [];
                for (let i = 1; i < stepNumber; i++) {
                    this.state.completedSteps.push(i);
                }

                if (stepNumber <= 2) this.state.wasStep2Skipped = false;

                // Force progress update
                this.builder.progressManager.updateProgress(stepNumber);

                await this.orchestrator.renderCurrentStep();
                await this.updateUrlForStepNavigation(stepNumber);
            } catch (error) {
                console.error(`Error navigating to step ${stepNumber}:`, error);
                this.builder.uiFeedback.showError("Could not navigate to the selected step.");
            } finally {
                this.isProcessingTransition = false;
                this.builder.uiFeedback.hideLoading();
            }
        }
    }

    async handlePopState(event) {
        if (this.state.isProgrammaticNavigation() || this.isProcessingTransition) {
            this.state.setProgrammaticNavigation(false);
            return;
        }

        const historyState = this.state.parseHistoryState(event);
        if (!historyState) {
            console.warn("[Popstate] Invalid history state. Re-initializing from URL.");
            await this.builder.initialize();
            return;
        }

        const { sharedConfigId, context, step: targetStep, wasStep2Skipped, selections } = historyState;

        if (targetStep === 2 && wasStep2Skipped) {
            window.history.back();
            return;
        }

        this.isProcessingTransition = true;
        this.builder.uiFeedback.showLoading();

        this.state.currentSharedConfigId = sharedConfigId || null;
        this.state.wasStep2Skipped = wasStep2Skipped || false;

        const apiPayload = this.buildApiPayloadForPopstate(sharedConfigId, targetStep, context, selections);
        this.state.setPreventURLUpdates(true);

        try {
            const responseData = await this.api.fetchData(apiPayload);
            this.state.updateStateFromApiResponse(responseData);
            this.state.currentStep = targetStep;
            this.state.completedSteps = Array.from({ length: targetStep - 1 }, (_, i) => i + 1);

            this.builder.progressManager.updateProgress(targetStep);
            await this.orchestrator.renderCurrentStep();

            // Apply selections if present
            await this.applySelectionsIfNeeded(selections);

        } catch (error) {
            console.error('[Popstate] Failed to restore state:', error);
            this.builder.uiFeedback.showError('There was a problem navigating. Please try refreshing.');
        } finally {
            this.state.setPreventURLUpdates(false);
            this.isProcessingTransition = false;
            this.builder.uiFeedback.hideLoading();
        }
    }

    // Helper Methods
    async updateUrlForStepNavigation(stepNumber, useReplace = true) {
        if (stepNumber >= 2) {
            // Check if urlManager exists before using buildSelectionsForURL
            const buildSelectionsFn = this.builder.urlManager?.buildSelectionsForURL?.bind(this.builder.urlManager) ||
                                     this.builder.cardManager?.buildSelectionsForURL?.bind(this.builder.cardManager) ||
                                     (() => {});

            const snapshotConfig = this.state.getCurrentConfigurationForSnapshot(buildSelectionsFn);
            if (snapshotConfig) {
                const snapshotData = await this.api.createSnapshot(snapshotConfig);
                this.state.updateUrlWithSnapshot(snapshotData.short_id, snapshotData.snapshot_id, useReplace);
            }
        } else {
            this.state.updateURLWithContext(useReplace, null);
        }
    }

    async handleSnapshotCreation(oldStep, newStep) {
        const stepActuallyChanged = newStep > oldStep;
        if (stepActuallyChanged && newStep >= 2) {
            // Check if urlManager exists before using buildSelectionsForURL
            const buildSelectionsFn = this.builder.urlManager?.buildSelectionsForURL?.bind(this.builder.urlManager) ||
                                     this.builder.cardManager?.buildSelectionsForURL?.bind(this.builder.cardManager) ||
                                     (() => {});

            const snapshotConfig = this.state.getCurrentConfigurationForSnapshot(buildSelectionsFn);
            if (snapshotConfig) {
                const snapshotData = await this.api.createSnapshot(snapshotConfig);
                if (snapshotData?.short_id) {
                    this.state.updateUrlWithSnapshot(snapshotData.short_id, snapshotData.snapshot_id, false);
                }
            }
        }
    }

    getContextForStep(stepNumber) {
        if (stepNumber === 2) {
            const currentContext = this.state.context?.context || {};
            return {
                "Device Make": currentContext['Device Make'],
                "Device Model": currentContext['Device Model']
            };
        }
        return {};
    }

    buildApiPayloadForPopstate(sharedConfigId, targetStep, context, selections) {
        if (sharedConfigId) {
            return { load_from_snapshot_id: sharedConfigId };
        } else if (targetStep === 1) {
            return {};
        } else {
            const payload = { ...context };
            if (selections && targetStep === 3) {
                payload.selections = selections;
            }
            return payload;
        }
    }

    async applySelectionsIfNeeded(selections) {
        if (this.state.currentStep === 3) {
            const selectionsToApply = selections || this.state.context?.selections;
            if (selectionsToApply) {
                try {
                    // Try urlManager first, fall back to cartAndShareManager
                    if (this.builder.urlManager && typeof this.builder.urlManager.applySelectionsFromURL === 'function') {
                        await this.builder.urlManager.applySelectionsFromURL(selectionsToApply);
                    } else if (this.builder.cartAndShareManager && typeof this.builder.cartAndShareManager.applySelectionsFromURL === 'function') {
                        await this.builder.cartAndShareManager.applySelectionsFromURL(selectionsToApply);
                    } else {
                        console.error('[GDSNavigationEventHandler] No manager found with applySelectionsFromURL method');
                    }
                } catch (error) {
                    console.error('[GDSNavigationEventHandler] Error applying selections:', error);
                    // If there's an error applying selections, fail gracefully
                    // The user will see the default state
                }
            }
        }
    }
}
